import { cn } from "@racoon/ui";

/** One tab in the segmented control. `value` is the discriminant used by parents (e.g. `"general"`). */
export type ViewSelectorOption<V extends string = string> = {
  readonly value: V;
  readonly label: string;
  readonly disabled?: boolean;
};

export type ViewSelectorProps<O extends readonly ViewSelectorOption<string>[]> = {
  options: O;
  activeSection: O[number]["value"];
  setActiveSection: (section: O[number]["value"]) => void;
  /** Smaller text / radius (e.g. three tabs on narrow layouts). */
  compact?: boolean;
};

const gridColsClass = (count: number) => {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  return "grid-cols-3";
};

/**
 * Segmented control for switching record detail sections.
 * Pass a readonly `options` tuple (e.g. `as const`) so `activeSection` is typed as the union of option values.
 * Intended for 1–3 tabs; more than 3 still render in a 3-column grid (second row as needed).
 */
export function ViewSelector<const O extends readonly ViewSelectorOption<string>[]>(
  props: ViewSelectorProps<O>,
) {
  const { options, activeSection, setActiveSection, compact = false } = props;
  return (
    <div
      className={cn(
        "grid gap-1 bg-brand-blue-light p-1",
        gridColsClass(options.length),
        compact ? "rounded-[10px]" : "rounded-lg",
      )}
    >
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          type="button"
          data-testid={`view-selector-${opt.value}`}
          onClick={() => setActiveSection(opt.value)}
          className={cn(
            "px-2 font-medium leading-[14px] tracking-tight transition-colors",
            compact ? "h-8 rounded-lg text-xs" : "h-8 rounded-md text-sm",
            activeSection === opt.value
              ? "bg-brand-blue text-text-invert shadow-sm"
              : "text-slate-900",
            opt.disabled ? "opacity-50 cursor-not-allowed" : "",
          )}
          disabled={opt.disabled}
          aria-disabled={opt.disabled}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
